import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import pick from 'lodash/pick';

import * as R from 'ramda'
// Local Deps
import history from '../../config/history';
import {
  DEFAULT_PAGINATION_SIZE,
  DEFAULT_DB_DATE_FORMAT,
} from '../../config/properties';
import { solicitationsSearchRoute } from '../../config/navigation/routes';
import materialFilters from '../../config/searchFilters/materialFilters';
import methodFilters from '../../config/searchFilters/methodFilters';
import { ninnsAndNsnsByCage } from '../../graphql/item';
import {
  requestSolicitationsSearch,
  requestBidMatchingSettings,
  getFolders,
  updateFolders,
  solicitationFolderList,
  requestSolicitationsAggregations,
  requestfolderAggregations,
  solicitationToolingList
} from './graphql';
// Utils
import {
  addFilterToParamsObject,
  removeFilterFromParamsObject,
  formatFiltersForApi,
  formatFiltersForUI,
} from '../../utils/filterGroupsUtils';
import { forwardTo } from '../../utils/browserHistoryUtils';
import { formatDate, addDaysAndFormat } from '../../utils/dateUtils';
import {
  createQueryString,
  parseQueryParams,
} from '../../utils/queryStringUtils';
import Subscription from '../../components/Subscription/Subscription';

export default function SolicitationsSearchHOC(SolicitationsPage) {
  class WrappedComponent extends Component {
    static propTypes = {
      location: PropTypes.object.isRequired,
      history: PropTypes.shape({
        replace: PropTypes.func.isRequired,
      }).isRequired,
      auth: PropTypes.shape({
        id: PropTypes.string,
        role: PropTypes.string,
        expiration: PropTypes.number,
        organizationId: PropTypes.string,
        cageCode: PropTypes.string.isRequired,
      }).isRequired,
      subscriptionLevel: PropTypes.string,
    };

    static defaultProps = {
      subscriptionLevel: '',
    };

    static initialState = {
      bidMatchingUrl: solicitationsSearchRoute,
      solicitationList: [],
      errors: [],
      meta: {
        hits: 0,
        size: DEFAULT_PAGINATION_SIZE,
        from: 0,
      },
      statusAggs: null,
      folderAggs: null,
      organizationsCage: null,
      userRole: null,
      outstandingSearchCount: 0,
      searchInputText: '',
      suspectCastingFlag: true,
      bidMatchingSettings: {},
      direction: null,
      tabAlreadyChanged: false,
      sortHeader: null,
      selectedFilters: {},
      cageCode: null,
      folderized: {},
      selectedTab: '',
      moveSolicitationsTo: "tbq",
      selectAll: 0,
      organizationsName: null,
      usersName: null,
      niins: null,
      nsns: null,
      searchText: null,
      flagged: [],
      selectedSolicitations: [],
      rawSelected: {},
      solicitationsToSend: {},
      showModal: false,
      hasInitialized: false, // Track if we've successfully initialized the page
    };

    /* ---------------------------------------
      TRANSFORM DATE FILTER PARAMS
    -----------------------------------------*/
    static _transformDateFilterParams = queryParams => {
      const transformedQueryParams = { ...queryParams };
      const today = new Date();

      // Transform issueDate into min + max values, and then
      // remove issueDate from queryParams object

      if (queryParams.issueDate) {
        const { issueDate } = queryParams;

        const minIssueDate = addDaysAndFormat(
          today,
          issueDate,
          DEFAULT_DB_DATE_FORMAT,
        );
        const maxIssueDate = formatDate(today, DEFAULT_DB_DATE_FORMAT);

        transformedQueryParams.minIssueDate = minIssueDate;
        transformedQueryParams.maxIssueDate = maxIssueDate;

        delete transformedQueryParams.issueDate;
      }
      if (queryParams.returnDate) {
        const { returnDate } = queryParams;

        const minReturnDate = formatDate(today, DEFAULT_DB_DATE_FORMAT);
        const maxReturnDate = addDaysAndFormat(
          today,
          returnDate,
          DEFAULT_DB_DATE_FORMAT,
        );
        transformedQueryParams.minReturnDate = minReturnDate;
        transformedQueryParams.maxReturnDate = maxReturnDate;

        delete transformedQueryParams.returnDate;
      }
      return transformedQueryParams;
    };

    state = WrappedComponent.initialState;

    /* ----------------------------------
      LIFECYCLE - COMPONENT DID MOUNT
    -------------------------------------*/
    async componentDidMount() {
      try {
        // Guard: Don't proceed if auth.id is not available
        // This can happen when redirecting after policy acceptance
        if (!this.props.auth || !this.props.auth.id) {
          console.log('componentDidMount: auth.id not available, will retry when auth becomes available');
          return;
        }

        await this._initializePage();
      } catch (error) {
        console.error('Error in componentDidMount:', error);
        // Reset loading state on error
        this.setState({ outstandingSearchCount: 0 });
      }
    }

    /* ----------------------------------
      INITIALIZE PAGE - Shared logic for mount and update
    -------------------------------------*/
    _initializePage = async () => {
      if (!this.props.auth || !this.props.auth.id) {
        return;
      }

      // Prevent multiple initializations
      if (this.state.hasInitialized) {
        return;
      }

      const userID = this.props.auth.id;
      console.log('Initializing page for user:', userID);
      const organizationId = this.props.auth.organizationId;
      const location = this.props.location;

      try {
        await this.retrieveToolsByCage();

        // If there is a query string (the user is coming to this page from a link)
        if (location.search) {
          await this._loadSearchFromQueryString();
          // Mark as initialized only after successful load
          this.setState({ hasInitialized: true });
          return;
        }

        // No query string - load bid match settings (default behavior)
        await this._loadBidMatchSettings(organizationId);
        // Mark as initialized only after successful load
        // Note: _loadBidMatchSettings may redirect, which will trigger componentDidUpdate
        // but that's handled separately and won't cause issues
        this.setState({ hasInitialized: true });
      } catch (error) {
        console.error('Error in _initializePage:', error);
        // Don't set hasInitialized on error, so we can retry
        throw error;
      }
    }




    /* ----------------------------------
      LIFECYCLE - COMPONENT DID UPDATE
    -------------------------------------*/
    async componentDidUpdate(prevProps) {
      // Handle URL query string changes
      if (prevProps.location.search !== this.props.location.search) {
        try {
          await this._loadSearchFromQueryString();
        } catch (error) {
          console.error('Error in componentDidUpdate:', error);
          // Reset loading state on error - necessary for error recovery
          // eslint-disable-next-line react/no-did-update-set-state
          this.setState({ outstandingSearchCount: 0 });
        }
      }

      // Handle auth.id becoming available after initial mount
      // This is critical for first-time login scenarios, especially in staging
      const prevAuthId = prevProps.auth && prevProps.auth.id;
      const currentAuthId = this.props.auth && this.props.auth.id;

      if (!prevAuthId && currentAuthId && !this.state.hasInitialized) {
        // auth.id just became available and we haven't initialized yet
        // This handles the case where componentDidMount ran before auth was ready
        console.log('Auth.id became available, initializing page...');
        try {
          await this._initializePage();
        } catch (error) {
          console.error('Error initializing page after auth.id became available:', error);
          // Reset loading state on error
          // eslint-disable-next-line react/no-did-update-set-state
          this.setState({ outstandingSearchCount: 0 });
        }
      }
    }
    retrieveToolsByCage = async () => {
      const cageCode = this.props.auth.cageCode;
      if (cageCode.startsWith("O")) return // User is a Goverment User
      const response = await ninnsAndNsnsByCage(cageCode);

      // Add null check for the response
      if (response && response.ninnsAndNsnsByCage) {
        const { niinsList } = response.ninnsAndNsnsByCage;
        const niins = niinsList || [];
        const nsns = [];
        if (niins.length) {
          this.setState({ niins, nsns, toolingList: true, cageCode });
        }
      }
    };

    _loadBidMatchSettings = async (organizationId) => {
      try {
        let searchText;

        const response = await requestBidMatchingSettings(organizationId);
        // Just take the first entry

        // Disable bid match settings for government users
        if (this.props.auth.role === 'GOVERNMENT_USER') {
          const bidMatchingSettings = {
            materials: [],
            methods: [],
            minimumEstimatedQuantity: 0,
            minimumEstimatedCost: 0,
          };
          this.setState({ bidMatchingSettings });
          return;
        }

        const bidMatchingSettings = (response && response.organization &&
          response.organization.matchSettingList) || {
          materials: [],
          methods: [],
          minimumEstimatedQuantity: 0,
          minimumEstimatedCost: 0,
        };

        // Only use materials/methods that are actually selected in bid match settings
        // Materials are primary - if materials are selected, only show those materials
        // If no materials are selected, methods become primary
        const material = bidMatchingSettings.materials && bidMatchingSettings.materials.length > 0
          ? formatFiltersForUI(bidMatchingSettings.materials, materialFilters.filters)
          : []; // No materials selected - methods will be primary
        const method = bidMatchingSettings.methods && bidMatchingSettings.methods.length > 0
          ? formatFiltersForUI(bidMatchingSettings.methods, methodFilters.filters)
          : []; // No methods selected
        const minQuantity = bidMatchingSettings.minimumQuantity;
        const minEstimatedCost = bidMatchingSettings.minimumEstimatedCost;
        // Update Url
        const queryParams = parseQueryParams(this.props.location);
        if (queryParams) {
          searchText = queryParams.searchText;

        }
        const matched = true;
        const status = 'matched'
        const url = `${solicitationsSearchRoute}?${createQueryString({
          matched,
          material,
          method,
          minQuantity,
          minEstimatedCost,
          searchText,
          status
        })}`;

        this.setState({ bidMatchingUrl: url, searchText: null });
        // Change the url
        history.replace(url);
      } catch (error) {
        console.error('Error in _loadBidMatchSettings:', error);
        // Reset loading state and set default bid matching settings
        const defaultBidMatchingSettings = {
          materials: [],
          methods: [],
          minimumEstimatedQuantity: 0,
          minimumEstimatedCost: 0,
        };
        this.setState({
          bidMatchingSettings: defaultBidMatchingSettings,
          outstandingSearchCount: 0
        });
      }
    }


    _getBidMatchSettings = async () => {
      // Disable bid match settings for government users
      if (this.props.auth.role === 'GOVERNMENT_USER') {
        return {
          materials: [],
          methods: [],
          minimumEstimatedQuantity: 0,
          minimumEstimatedCost: 0,
        };
      }

      const response = await requestBidMatchingSettings(this.props.auth.organizationId);
      // Just take the first entry
      const bidMatchingSettings = (response.organization &&
        response.organization.matchSettingList) || {
        materials: [],
        methods: [],
        minimumEstimatedQuantity: 0,
        minimumEstimatedCost: 0,
      };

      const material = formatFiltersForUI(
        bidMatchingSettings.materials,
        materialFilters.filters,
      );
      const method = formatFiltersForUI(
        bidMatchingSettings.methods,
        methodFilters.filters,
      );
      const minQuantity = bidMatchingSettings.minimumQuantity;
      const minQuantity1 = bidMatchingSettings.minimumQuantity;

      const minEstimatedCost = bidMatchingSettings.minimumEstimatedCost;
      // Update Url
      const status = 'matched'

      const url = `${solicitationsSearchRoute}?${createQueryString({
        minQuantity1, // this line is a quick fix for a bug in the create query string 
        material,
        method,
        minQuantity,
        minEstimatedCost,
        status
      })}`;
      return url;
    }


    /* ----------------------------------
      FETCH MOST RECENT SEARCH
    -------------------------------------*/
    _loadSearchFromQueryString = async () => {
      try {
        // Guard: Don't proceed if auth.id is not available
        if (!this.props.auth || !this.props.auth.id) {
          console.log("_loadSearchFromQueryString: auth.id not available, skipping");
          return;
        }

        console.log("_loadSearchFromQueryString");
        const res = await getFolders(this.props.auth.id, this.props.auth.organizationId);
        console.log(res);

        // Add null checks
        let folders;
        let organizationsName = '';
        let usersName = '';

        if (!res || !res.user) {
          console.error('No user data returned from getFolders');
          folders = {
            'flagged': [],
            'tbq': [],
            'inprocess': [],
            'archived': [],
            'ace': [],
            'aceDates': {},
          };
          this.setState({
            folderized: folders
          });
          await updateFolders(folders, this.props.auth.id);
        } else {
          folders = res.user.folders;
          organizationsName = (res.organization && res.organization.name) || '';
          usersName = res.user.name || '';

          // Ensure ace folder exists for existing users
          if (folders && !folders.ace) {
            folders.ace = [];
            // Update the database to add the ace folder
            await updateFolders(folders, this.props.auth.id);
          }
          // Ensure aceDates exists
          if (folders && !folders.aceDates) {
            folders.aceDates = {};
          }
        }
        // const cage = this.props.auth.cageCode
        // if (orgTooling.organization) {
        //   orgTooling = orgTooling.organization.tools.map(obj => obj.nsn);
        // }
        if (folders) {
          this.setState({
            folderized: folders,
            organizationsCage: this.props.auth.cageCode,
            userRole: this.props.auth.role,
            organizationsName,
            usersName
          })
        } else {
          folders = {
            'flagged': [],
            'tbq': [],
            'inprocess': [],
            'archived': [],
            'ace': [],
            'aceDates': {},
          }
          this.setState({
            folderized: folders
          })
          await updateFolders(folders, this.props.auth.id)
        }
        const { location } = this.props;
        let queryParams = parseQueryParams(location);

        // Redirect government users away from matched tab
        if (queryParams && queryParams.matched && this.props.auth.role === 'GOVERNMENT_USER') {
          // Remove matched parameter and redirect to open tab
          const newQueryParams = { ...queryParams };
          delete newQueryParams.matched;
          newQueryParams.open = true;
          const newUrl = `${location.pathname}?${createQueryString(newQueryParams)}`;
          this.props.history.replace(newUrl);
          return;
        }

        if (queryParams && queryParams.searchText) {
          console.log(this.state.searchText, queryParams.searchText)
          if (this.state.searchText !== queryParams.searchText) {
            queryParams.from = 0;
          }
          if (typeof queryParams.searchText === 'string') {
            queryParams.searchText = queryParams.searchText.trim();
          }
          this.setState({ searchText: queryParams.searchText })

        }
        queryParams = { ...queryParams, folders };
        await this._executeSearch(queryParams || {});
      } catch (error) {
        console.error('Error in _loadSearchFromQueryString:', error);
        // Reset loading state and ensure component can render
        this.setState({
          outstandingSearchCount: 0,
          solicitationList: [],
          errors: [error.message || 'Failed to load search results']
        });
      }
    };

    /* ----------------------------------
      EXECUTE SEARCH
    -------------------------------------*/

    _executeBidMatchingSearchForAggs = async (searchText) => {
      const url = await this._getBidMatchSettings()
      const queryParams = parseQueryParams(url);
      const {
        from,
        size = DEFAULT_PAGINATION_SIZE,
        header,
        folder,
        status,
        ...selectedFilters


      } = queryParams;

      const transformedFilters = WrappedComponent._transformDateFilterParams(
        selectedFilters,
      );



      const {
        material: materials = [],
        method: methods = [],

        minQuantity,
        minEstimatedCost,
      } = transformedFilters;

      /*
      * Bid matching settings are a subset of the selectedFilters,
      * and are special because their values cannot be changed from
      * the filters menu.
      */
      // Use "pick" fo filter out null/empty values vvv


      // Manage loading in state


      // Format materials/methods into expected values from server
      // Always respect bid match settings - only show selected materials/methods
      // Materials are primary: if materials are selected, only show those materials
      // If no materials are selected, methods become primary
      const formattedMaterials = materials && materials.length > 0
        ? formatFiltersForApi(materials, materialFilters.filters)
        : [];
      const formattedMethods = methods && methods.length > 0
        ? formatFiltersForApi(methods, methodFilters.filters)
        : [];

      // Request solicitations
      const {

        meta,
      } = await requestSolicitationsSearch({
        nsns: this.state.nsns,
        niins: this.state.niins,
        freeText: searchText,
        from,
        size,
        materials: formattedMaterials,
        methods: formattedMethods,
        PurchaseRequestStatus: 'matched',
        minQuantity,
        minEstimatedCost,

      });
      return meta.hits




      // Persist solictation search

    };


    _executeFolderSearch = async (queryParams) => {

      const {
        searchText = '',

        from,
        size = DEFAULT_PAGINATION_SIZE,
        header,
        folder,
        folders,
        direction,
        ...selectedFilters

      } = queryParams;

      // For "Date Sent to ACE", don't send sort to backend (field doesn't exist in Elasticsearch)
      // We'll do client-side sorting after enrichment
      let sortHeader = header;
      let sortDirection = direction;
      if (header === "Date Sent to ACE") {
        // Use default sort for backend query, we'll sort client-side later
        sortHeader = "issue_date";
        sortDirection = "DESC";
      } else {
        switch (header) {
          case "Date":
            sortHeader = "issue_date"
            break;
          case "Solicitation #":
            sortHeader = "solicitation_id"
            break;
          case "Close Date":
            sortHeader = "return_date"
            break;
          case "Status":
            sortHeader = "status"
            break;
          case "NSN":
            sortHeader = "purchase_request.nsn_part"
            break;
          case "Last Award Date":
            sortHeader = "lastAward"
            break;
          case "Description":
            sortHeader = "purchase_request.nomenclature.raw"
            break;
          case "Qty":
            sortHeader = "purchase_request.quantity"
            break;
          case "UI":
            sortHeader = "um"
            break;
          case "Est. Value":
            sortHeader = "est_value"
            break;
          case "SRVA":
            sortHeader = "srva"
            break;
          case "AMC/AMSC":
            sortHeader = "amc"
            break;
          case "Material":
            sortHeader = "purchase_request.material.keyword"
            break;
          case "Method":
            sortHeader = "purchase_request.method.keyword"
            break;
          case "Type":
            sortHeader = "casting_type.keyword"
            break;
          case "Lead":
            sortHeader = "purchase_request.nomenclature.raw"
            break;
          case "Award Date":
            sortHeader = "lastAward"
            break;
          default:
            break;
        }
      }
      this.setState(oldState => ({
        outstandingSearchCount: oldState.outstandingSearchCount + 1,
        selectedTab: folder
      }));

      const transformedFilters = WrappedComponent._transformDateFilterParams(
        selectedFilters,
      );

      const {
        material: materials = [],
        method: methods = [],
        minQuantity,
        minEstimatedCost,
        minIssueDate,
        maxIssueDate,
        minReturnDate,
        maxReturnDate,
        suspectCastingFlag,
      } = transformedFilters;

      let toolingHits = 0;
      if (this.state.toolingList) {
        try {
          // Format materials/methods for tooling search - respect bid match settings
          const formattedMaterials = materials && materials.length > 0
            ? formatFiltersForApi(materials, materialFilters.filters)
            : [];
          const formattedMethods = methods && methods.length > 0
            ? formatFiltersForApi(methods, methodFilters.filters)
            : [];

          const matchedSolicitations = await solicitationToolingList({
            freeText: searchText,
            nsns: this.state.nsns,
            niins: this.state.niins,
            from,
            size,
            materials: formattedMaterials,
            methods: formattedMethods,
            minQuantity,
            minEstimatedCost,
          })
          toolingHits = (matchedSolicitations && matchedSolicitations.solicitationToolingList && matchedSolicitations.solicitationToolingList.meta && matchedSolicitations.solicitationToolingList.meta.hits) || 0;
        } catch (toolingError) {
          console.error('Error fetching tooling solicitations in folder search:', toolingError);
          // Continue with 0 toolingHits
        }
      }
      try {



        const res = await solicitationFolderList({
          folder: folders[folder],
          from,
          size,
          sortHeader,
          direction: sortDirection,
          minIssueDate,
          maxIssueDate,
          minReturnDate,
          maxReturnDate,
          suspectCastingFlag,
          freeText: searchText,

        });

        let statusAggs = await requestSolicitationsAggregations({
          freeText: searchText,
        })
        // Add null check for response structure
        statusAggs = (statusAggs && statusAggs.solicitationAggregation && statusAggs.solicitationAggregation[0] && statusAggs.solicitationAggregation[0].statusBuckets) || [];
        statusAggs = statusAggs.filter(bucket => bucket.key !== 'matched');

        let folderAggs = [];
        // Only fetch folder aggregations if auth.id is available
        if (this.props.auth && this.props.auth.id) {
          try {
            folderAggs = await requestfolderAggregations({
              freeText: searchText,
              userId: this.props.auth.id
            });
            // Add null check for response structure
            folderAggs = (folderAggs && folderAggs.solicitationAggregation && folderAggs.solicitationAggregation[0] && folderAggs.solicitationAggregation[0].folderBuckets) || [];
          } catch (folderAggError) {
            console.error('Error fetching folder aggregations:', folderAggError);
            // Preserve existing folderAggs if available, otherwise use empty array
            folderAggs = this.state.folderAggs || [];
          }
        } else {
          // If auth.id is not available, preserve existing folderAggs
          folderAggs = this.state.folderAggs || [];
        }


        let matchedAggs = 0;
        try {
          matchedAggs = await this._executeBidMatchingSearchForAggs(searchText);
        } catch (aggError) {
          console.error('Error fetching matched aggregations in folder search:', aggError);
          // Continue with 0 matchedAggs
        }
        matchedAggs += toolingHits;
        const prevMatched = (this.state.statusAggs || []).find(a => a.key === 'matched');
        const prevMatchedCount = prevMatched ? prevMatched.docCount : 0;
        const matchedToShow = (matchedAggs === 0 && prevMatchedCount > 0) ? prevMatchedCount : matchedAggs;
        statusAggs.push({ key: "matched", "docCount": matchedToShow });
        // Format bidmatching settings

        this.setState(oldState => {
          if (oldState.outstandingSearchCount === 1) {
            // Check if response is valid
            if (!res || !res.solicitationFolderList) {
              return {
                outstandingSearchCount: 0,
              };
            }

            // Enrich solicitations with ACE dates if viewing ACE folder
            let enrichedSolicitations = res.solicitationFolderList.solicitationList || [];
            if (folder === 'ace' && oldState.folderized && oldState.folderized.aceDates) {
              enrichedSolicitations = enrichedSolicitations.map(slct => {
                const aceDate = oldState.folderized.aceDates[slct.id] ||
                  oldState.folderized.aceDates[slct.solicitationNum];
                if (aceDate) {
                  return { ...slct, dateSentToAce: aceDate };
                }
                return slct;
              });
            }

            // If sorting by "Date Sent to ACE", apply client-side sort after enrichment
            if (header === "Date Sent to ACE" && direction) {
              enrichedSolicitations = [...enrichedSolicitations].sort((a, b) => {
                const dateA = a.dateSentToAce || '';
                const dateB = b.dateSentToAce || '';

                // Empty dates go to the end
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;

                // Compare dates
                const comparison = dateA.localeCompare(dateB);

                // Apply sort direction
                if (direction === 'DESC') {
                  return -comparison;
                }
                return comparison; // ASC or default
              });
            }

            return {
              statusAggs,
              folderAggs,
              solicitationList: enrichedSolicitations,
              selectedFilters,
              selectedTab: folder,
              meta: res.solicitationFolderList.meta || oldState.meta,
              outstandingSearchCount: 0,
            };
          }
          return {
            outstandingSearchCount: oldState.outstandingSearchCount - 1,

          };
        });
      } catch (error) {
        this.setState({ outstandingSearchCount: 0 })
      }

    }

    _removeDuplicates = arr => {
      const set = Array.from(new Set(arr.map(a => a.id))).map(id => arr.find(a => a.id === id));

      return set
    }



    _executeSearch = async queryParams => {
      const {
        searchText = '',
        from,
        size = DEFAULT_PAGINATION_SIZE,
        direction,
        header,
        folder,
        matched,
        status,
        ...selectedFilters


      } = queryParams;
      if (folder) {
        this._executeFolderSearch(queryParams);
        return
      }
      const transformedFilters = WrappedComponent._transformDateFilterParams(
        selectedFilters,
      );



      const {
        material: materials = [],
        method: methods = [],
        minIssueDate,
        maxIssueDate,
        minReturnDate,
        maxReturnDate,
        minQuantity,
        minEstimatedCost,
        suspectCastingFlag,
      } = transformedFilters;

      /*
      * Bid matching settings are a subset of the selectedFilters,
      * and are special because their values cannot be changed from
      * the filters menu.
      */
      // Use "pick" fo filter out null/empty values vvv
      const bidMatchingSettings = pick(
        {
          materials,
          methods,
          minQuantity,
          minEstimatedCost,
        },
        ['materials', 'methods', 'minQuantity', 'minEstimatedCost'],
      );

      // Manage loading in state
      this.setState(oldState => ({
        outstandingSearchCount: oldState.outstandingSearchCount + 1,
      }));

      // Format materials/methods into expected values from server
      // Always respect bid match settings - only show selected materials/methods
      // Materials are primary: if materials are selected, only show those materials
      // If no materials are selected, methods become primary
      const formattedMaterials = materials && materials.length > 0
        ? formatFiltersForApi(materials, materialFilters.filters)
        : [];
      const formattedMethods = methods && methods.length > 0
        ? formatFiltersForApi(methods, methodFilters.filters)
        : [];
      // For "Date Sent to ACE", don't send sort to backend (field doesn't exist in Elasticsearch)
      let sortHeader = header;
      if (header === "Date Sent to ACE") {
        // Use default sort for backend query
        sortHeader = "issue_date";
      } else {
        switch (header) {
          case "Date":
            sortHeader = "issue_date"
            break;
          case "Solicitation #":
            sortHeader = "solicitation_id"
            break;
          case "Close Date":
            sortHeader = "return_date"
            break;
          case "Status":
            sortHeader = "status"
            break;
          case "NSN":
            sortHeader = "purchase_request.nsn_part"
            break;
          case "Last Award Date":
            sortHeader = "lastAward"
            break;
          case "Description":
            sortHeader = "purchase_request.nomenclature.raw"
            break;
          case "Qty":
            sortHeader = "purchase_request.quantity"
            break;
          case "UI":
            sortHeader = "um"
            break;
          case "Est. Value":
            sortHeader = "est_value"
            break;
          case "SRVA":
            sortHeader = "srva"
            break;
          case "AMC/AMSC":
            sortHeader = "amc"
            break;
          case "Material":
            sortHeader = "purchase_request.material.keyword"
            break;
          case "Method":
            sortHeader = "purchase_request.method.keyword"
            break;
          case "Type":
            sortHeader = "casting_type.keyword"
            break;
          case "Lead":
            sortHeader = "purchase_request.nomenclature.raw"
            break;
          case "Award Date":
            sortHeader = "lastAward"
            break;
          default:
            break;
        }
      }
      // Request solicitations
      try {
        let statusAggs = await requestSolicitationsAggregations({
          freeText: searchText,
        })
        // Add null check for response structure
        statusAggs = (statusAggs && statusAggs.solicitationAggregation && statusAggs.solicitationAggregation[0] && statusAggs.solicitationAggregation[0].statusBuckets) || [];
        // Remove any "matched" from API so our computed matched count is the single source of truth
        statusAggs = statusAggs.filter(bucket => bucket.key !== 'matched');
        let newTab = status;
        if (searchText) {
          newTab = this.determineTab(statusAggs)
          if (!newTab) {
            newTab = status;
          }

        }
        console.log(newTab)


        const {
          solicitationList,
          errors,
          meta,
        } = await requestSolicitationsSearch({

          freeText: searchText,
          from,
          size,
          materials: formattedMaterials,
          methods: formattedMethods,
          PurchaseRequestStatus: newTab,
          minIssueDate,
          maxIssueDate,
          minReturnDate,
          maxReturnDate,
          minQuantity,
          minEstimatedCost,
          suspectCastingFlag,
          direction,
          sortHeader
        });

        let folderAggs = [];
        // Only fetch folder aggregations if auth.id is available
        if (this.props.auth && this.props.auth.id) {
          try {
            folderAggs = await requestfolderAggregations({
              freeText: searchText,
              userId: this.props.auth.id
            });
            // Add null check for response structure
            folderAggs = (folderAggs && folderAggs.solicitationAggregation && folderAggs.solicitationAggregation[0] && folderAggs.solicitationAggregation[0].folderBuckets) || [];
          } catch (folderAggError) {
            console.error('Error fetching folder aggregations:', folderAggError);
            // Preserve existing folderAggs if available, otherwise use empty array
            folderAggs = this.state.folderAggs || [];
          }
        } else {
          // If auth.id is not available, preserve existing folderAggs
          folderAggs = this.state.folderAggs || [];
        }

        let toolingSolicitationList = [];
        let toolingHits = 0;
        if (this.state.toolingList) {
          try {
            // Use the already formatted materials/methods from above
            // (formattedMaterials and formattedMethods are already declared in this scope)
            const matchedSolicitations = await solicitationToolingList({
              freeText: searchText,
              nsns: this.state.nsns,
              niins: this.state.niins,
              from,
              size,
              materials: formattedMaterials,
              methods: formattedMethods,
              minQuantity,
              minEstimatedCost,
            })
            toolingHits = (matchedSolicitations && matchedSolicitations.solicitationToolingList && matchedSolicitations.solicitationToolingList.meta && matchedSolicitations.solicitationToolingList.meta.hits) || 0;

            toolingSolicitationList = (matchedSolicitations && matchedSolicitations.solicitationToolingList && matchedSolicitations.solicitationToolingList.solicitationList) || [];
          } catch (toolingError) {
            console.error('Error fetching tooling solicitations:', toolingError);
            // Continue with empty tooling list
          }
        }


        let combinedList = [];

        if (!direction) {
          combinedList = [...toolingSolicitationList, ...solicitationList]
          combinedList = await this._removeDuplicates(combinedList);

        } else {
          combinedList = [...solicitationList]
        }

        // When on Matched tab with no materials or methods selected for bid matching, show empty results
        const isEmptySelection = (arr) =>
          !arr || arr.length === 0 || arr.every((v) => !v || (typeof v === 'string' && !String(v).trim()));
        const noBidMatchingSelected = isEmptySelection(materials) && isEmptySelection(methods);
        if (newTab === 'matched' && noBidMatchingSelected) {
          combinedList = [];
        }

        let matchedAggs = 0;
        try {
          matchedAggs = await this._executeBidMatchingSearchForAggs(searchText);
        } catch (aggError) {
          console.error('Error fetching matched aggregations:', aggError);
          // Continue with 0 matchedAggs
        }

        matchedAggs += toolingHits;
        // Keep the previous Matched count when on another tab so the number doesn't disappear (e.g. on fetch failure)
        const previousMatched = (this.state.statusAggs || []).find(a => a.key === 'matched');
        const previousMatchedCount = previousMatched ? previousMatched.docCount : 0;
        const computedMatchedCount = noBidMatchingSelected ? 0 : matchedAggs;
        const matchedCountToShow = (newTab !== 'matched' && computedMatchedCount === 0 && previousMatchedCount > 0)
          ? previousMatchedCount
          : computedMatchedCount;
        statusAggs.push({ key: "matched", "docCount": matchedCountToShow });

        if (newTab === 'matched') {
          meta.hits = noBidMatchingSelected ? 0 : matchedAggs
        }
        // Format bidmatching settings

        console.log(combinedList)
        this.setState(oldState => {
          if (oldState.outstandingSearchCount === 1) {
            return {
              statusAggs,
              folderAggs,
              bidMatchingSettings,
              selectedFilters,
              searchInputText: searchText,
              solicitationList: combinedList,
              direction,
              sortHeader: header,
              errors,
              meta,
              outstandingSearchCount: 0,
              suspectCastingFlag,
            };
          }
          return {
            outstandingSearchCount: oldState.outstandingSearchCount - 1,
          };
        });


        this.setState({
          selectedTab: newTab
        })
      } catch (error) {
        console.error('Error in _executeSearch:', error);
        // Reset loading state and show error
        this.setState(oldState => {
          const newCount = Math.max(0, oldState.outstandingSearchCount - 1);
          return {
            outstandingSearchCount: newCount,
            errors: [error.message || 'Failed to execute search'],
            solicitationList: oldState.solicitationList || [], // Keep existing list if available
          };
        });
      }

    };



    determineTab = (statusAggs) => {
      const statusObj = {};
      // statusAggs probably should of been an object instead of an array of to begin with but such is life 
      const currentTab = this.state.selectedTab
      if (this.state.tabAlreadyChanged) {
        return null;
      }
      statusAggs.map(statusAgg => {
        statusObj[statusAgg.key] = statusAgg.docCount
        return '';
      })
      if (statusObj[currentTab]) {
        return currentTab;
      }
      // Skip matched tab for government users
      else if (statusObj.matched && this.props.auth.role !== 'GOVERNMENT_USER') {
        return 'matched'
      }
      else if (statusObj.open) {
        return 'open'
      }
      else if (statusObj.closed) {
        return 'closed'
      }
      else if (statusObj.awarded) {
        return 'awarded'
      }
      else if (statusObj.cancelled) {
        return 'cancelled'
      }

      // Default to 'open' for government users instead of 'matched'
      return this.props.auth.role === 'GOVERNMENT_USER' ? 'open' : 'matched'



    }
    /* ----------------------------------
      HANDLER - FILTER CLICK
    -------------------------------------*/
    _onFilterClick = (filter, groupKey) => {
      const { location } = this.props;
      const { value } = filter;
      let url;

      const queryParams = parseQueryParams(location);
      // check if our query pSolicitationsGridrams for the presence of a selected filter.
      // If a filte ris foundSolicitationsGridto already be selected., go ahead and
      // remove that filter fSolicitationsGridom the query params object.

      if (
        (queryParams[groupKey] || queryParams[groupKey] === 0) &&
        queryParams[groupKey] === value
      ) {
        const newParams = removeFilterFromParamsObject({
          complexObject: queryParams,
          groupKey,
          filter,
        });
        url = `${solicitationsSearchRoute}?${createQueryString(newParams)}`;
      } else {
        // This condition is for first-time selection within a filter group, or when a user
        // is selecting a different filter from a filter group

        const newParams = addFilterToParamsObject({
          complexObject: queryParams,
          groupKey,
          filter,
        });
        url = `${solicitationsSearchRoute}?${createQueryString(newParams)}`;
      }
      // Change the url
      forwardTo(url);
    };

    /* ----------------------------------
      HANDLER - CLEAR-ALL BUTTON CLICK
    -------------------------------------*/
    _onClearAllFiltersClick = () => {
      const { bidMatchingUrl } = this.state;

      forwardTo(bidMatchingUrl);
      // this.forceUpdate()
    };


    /* ----------------------------------
      HANDLER - SEARCH BUTTON CLICK
    -------------------------------------*/
    _onSearchButtonClick = async searchText => {
      const { location } = this.props;
      const queryParams = {
        ...parseQueryParams(location),
        searchText,
      };
      const url = `${solicitationsSearchRoute}?${createQueryString(
        queryParams,
      )}`;
      // Change the url
      forwardTo(url);
    };

    /* ----------------------------------
      HANDLER - PAGE CHANGE
    -------------------------------------*/
    _onPageChange = index => {
      const { location } = this.props;
      const { meta } = this.state;

      const from = index * meta.size;
      const size = meta.size;
      // Format params
      const queryParams = {
        ...parseQueryParams(location),
        from,
        size,
      };
      const url = `${solicitationsSearchRoute}?${createQueryString(
        queryParams,
      )}`;
      // Change the url
      forwardTo(url);
    };

    _onSortClick = (header, direction) => {
      if (header === "Folder") {
        return
      }

      // Handle client-side sorting for "Date Sent to ACE" since it's not in Elasticsearch
      if (header === "Date Sent to ACE") {
        const { location } = this.props;
        const newDirection = direction || 'ASC';

        this.setState(oldState => {
          const { solicitationList } = oldState;

          if (!solicitationList || solicitationList.length === 0) {
            return {
              sortHeader: header,
              direction: newDirection
            };
          }

          // Create a sorted copy of the list
          const sortedList = [...solicitationList].sort((a, b) => {
            const dateA = a.dateSentToAce || '';
            const dateB = b.dateSentToAce || '';

            // Empty dates go to the end
            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;

            // Compare dates
            const comparison = dateA.localeCompare(dateB);

            // Apply sort direction
            if (newDirection === 'DESC') {
              return -comparison;
            }
            return comparison; // ASC or default
          });

          return {
            sortHeader: header,
            direction: newDirection,
            solicitationList: sortedList
          };
        });

        // Update URL to reflect sort state
        const queryParams = {
          ...parseQueryParams(location),
          header,
          direction: newDirection,
        };
        const url = `${solicitationsSearchRoute}?${createQueryString(queryParams)}`;
        forwardTo(url);
        return;
      }

      // For other columns, use server-side sorting
      this.setState({
        sortHeader: header,
        direction
      })
      const { location } = this.props;

      // Format params
      const queryParams = {
        ...parseQueryParams(location),
        header,
        direction,
      };

      const url = `${solicitationsSearchRoute}?${createQueryString(
        queryParams,
      )}`;
      // Change the url
      forwardTo(url);
    };

    /* ----------------------------------
      HANDLER - PAGE SIZE CHANGE
    -------------------------------------*/
    _onPageSizeChange = (size, index) => {
      const { location } = this.props;

      const from = index * size;
      // Format params
      const queryParams = {
        ...parseQueryParams(location),
        from,
        size,
      };
      const url = `${solicitationsSearchRoute}?${createQueryString(
        queryParams,
      )}`;
      // Change the url
      forwardTo(url);
    };

    /* ----------------------------------
      HANDLER - SEARCH INPUT KEY DOWN
    -------------------------------------*/
    _onSearchInputKeyDown = async (event, searchText) => {
      const { location } = this.props;
      // Handle "Enter" key
      if (event.key === 'Enter') {
        // Stop default event bubbling
        event.preventDefault();
        event.stopPropagation();
        // Format params
        const queryParams = {
          ...parseQueryParams(location),
          searchText,
        };
        const url = `${solicitationsSearchRoute}?${createQueryString(
          queryParams,
        )}`;
        // Change the url
        forwardTo(url);
      }
    };

    /* ----------------------------------
      HANDLER - SEARCH INPUT CHANGE
    -------------------------------------*/
    _onSearchInputChange = e => {
      const searchInputText = e.target.value;
      this.setState({
        searchInputText,
      });
    };

    /* ----------------------------------
      HANDLER - SUSPECT CASTING SWITCH CHANGE
    -------------------------------------*/
    _onSuspectCastingSwitchClick = checked => {
      const { location } = this.props;

      const queryParams = {
        ...parseQueryParams(location),
        suspectCastingFlag: checked,
      };

      const url = `${solicitationsSearchRoute}?${createQueryString(
        queryParams,
      )}`;

      forwardTo(url);

      this.setState({
        suspectCastingFlag: checked,
      });
    };

    downloadSelected = showDownload => {
      this.setState({
        showDownload,
      });
    };

    openModal = () => {
      this.setState({
        showModal: true,
      });
    };

    flagged = async (solictation) => {
      const flaggedArray = { ...this.state.folderized }
      const index = flaggedArray.flagged.indexOf(solictation);
      if (index !== -1) {
        flaggedArray.flagged.splice(index, 1);
        this.setState({ folderized: flaggedArray })
      }
      else {

        flaggedArray.flagged.push(solictation)
        this.setState({
          folderized: flaggedArray
        })
      }
      await updateFolders(flaggedArray, this.props.auth.id);
    }

    tabChanged = (key) => {
      // Prevent government users from accessing the matched tab
      if (key === 'matched' && this.props.auth.role === 'GOVERNMENT_USER') {
        return;
      }

      const organizationId = this.props.auth.organizationId;
      let tabAlreadyChanged;
      let url;
      const queryParams = {
        ...parseQueryParams(this.props.location),

      };
      const picked = (({ searchText }) => ({ searchText }))(queryParams);
      console.log(picked)

      if (picked.searchText && !tabAlreadyChanged) {
        tabAlreadyChanged = true;
      } else {
        tabAlreadyChanged = false;
      }



      this.setState({
        selectedTab: key,
        selectedSolicitations: [],
        selectAll: 0,
        rawSelected: {},
        selectedFilters: {},
        tabAlreadyChanged
      })

      switch (key) {
        case "open":
        case "closed":
        case "cancelled":
        case "awarded":

          picked.status = key;


          url = `${solicitationsSearchRoute}?${createQueryString(
            picked,
          )}`;
          // Change the url
          // forwardTo(url);
          break;
        case "matched":
          this._loadBidMatchSettings(organizationId)
          return;



        default:
          picked.folder = key;


          url = `${solicitationsSearchRoute}?${createQueryString(
            picked,
          )}`;

          break;
      }


      // Change the url
      forwardTo(url);
    }

    _getSelectedSolicitations = (solicitationArr) => {
      const newArr = [...solicitationArr]
      this.setState({
        selectedSolicitations: newArr
      }
      )
    }


    test = async () => {
      const res = await solicitationFolderList({
        folder: this.state.selectedSolicitations,

      });
      this.setState({
        solicitationsToSend: res.solicitationFolderList
      })

    }


    _moveSolicitations = async (selectedMenu) => {

      const flaggedArray = { ...this.state.folderized }
      if (selectedMenu === "mail") {
        this.test();
        return
      }

      if (selectedMenu === "folder") {
        Object.keys(flaggedArray).forEach(item => {
          if (this.state.moveSolicitationsTo === item) {
            const unique = R.uniq(this.state.selectedSolicitations, flaggedArray[item])
            flaggedArray[item] = [...new Set([...flaggedArray[item], ...unique])]
          }
          else if (item !== "flagged") {


            flaggedArray[item] = R.without(this.state.selectedSolicitations, flaggedArray[item])

          }
        }

        );
      } else if (selectedMenu === "flag") {
        const unique = R.uniq(this.state.selectedSolicitations, flaggedArray.flagged)
        flaggedArray.flagged = [...new Set([...flaggedArray.flagged, ...unique])];
      }
      else if (selectedMenu === "unflag") {
        const unique = R.without(this.state.selectedSolicitations, flaggedArray.flagged)
        flaggedArray.flagged = unique
      }

      this.setState({
        folderized: flaggedArray
      })
      await updateFolders(flaggedArray, this.props.auth.id)
      await this._loadSearchFromQueryString()
    }

    _changeSelection = (key) => {

      this.setState({
        moveSolicitationsTo: key
      })
    }


    _closePopUp = () => {
      this.setState({
        selectedSolicitations: [],
        selectAll: 0,
        rawSelected: {},

      })
    }

    _changeSelectAllValue = (rawSelected, value) => {
      this.setState({
        rawSelected,
        selectAll: value
      })
    }

    _closeModal = (_, msg) => {

      this.setState({
        solicitationsToSend: []
      })
      if (msg) {
        if (msg === "success") {
          this._closePopUp();
        }
      }
    }

    /* ----------------------------------
      RENDER
    -------------------------------------*/
    render() {
      const {
        solicitationList,
        outstandingSearchCount,
        meta,
        errors,
        searchInputText,
        suspectCastingFlag,
        selectedFilters,
        bidMatchingSettings,
        showDownload,
        showModal,
        direction,
        sortHeader,
        folderized,
        selectedSolicitations,
        selectAll,
        rawSelected,
        selectedTab,
        solicitationsToSend,
        organizationsCage,
        userRole,
        organizationsName,
        usersName,
        statusAggs,
        folderAggs,
        cageCode
      } = this.state;

      // Configure Page
      const pageProps = {
        cageCode,
        statusAggs,
        folderAggs,
        usersName,
        organizationsName,
        solicitationsToSend,
        organizationsCage,
        userRole,
        selectedTab,
        selectAll,
        folderized,
        selectedSolicitations,
        direction,
        sortHeader,
        rawSelected,
        changeSelection: this._changeSelection,
        moveSolicitations: this._moveSolicitations,
        handleOk: this.handleOk,
        handleCancel: this.handleCancel,
        openModal: this.openModal,
        downloadSelected: this.downloadSelected,
        solicitations: solicitationList,
        closePopUp: this._closePopUp,
        showDownload,
        meta,
        showModal,
        errors,
        changeSelectAllValue: this._changeSelectAllValue,
        onSortClick: this._onSortClick,
        selectedFilters,
        isLoading: outstandingSearchCount > 0,
        suspectCastingFlag,
        getSelectedSolicitations: this._getSelectedSolicitations,
        searchText: searchInputText,
        bidMatchingSettings,
        onClearAllFiltersClick: this._onClearAllFiltersClick,
        onFilterClick: this._onFilterClick,
        tabChanged: this.tabChanged,
        onSearchButtonClick: this._onSearchButtonClick,
        onSearchInputKeyDown: this._onSearchInputKeyDown,
        onPageChange: this._onPageChange,
        onPageSizeChange: this._onPageSizeChange,
        onSearchInputChange: this._onSearchInputChange,
        onSuspectCastingSwitchClick: this._onSuspectCastingSwitchClick,
        flagged: this.flagged,
        closeModal: this._closeModal,
        auth: this.props.auth,
        subscriptionLevel: this.props.subscriptionLevel,
      };

      return (
        <Subscription service="Solicitations" level="ICON Core">
          <SolicitationsPage {...pageProps} />
        </Subscription>
      );
    }
  }

  return withRouter(WrappedComponent);
}







// WEBPACK FOOTER //
// ./src/pages/SolicitationsSearch/SolicitationsSearchHOC.jsx